/**
 * Local DXF Uploader Component
 * Modern file upload interface with drag-and-drop support for DXF/CAD files
 */

import { useState, useRef, DragEvent } from 'react'
import styles from './LocalCSVUploader.module.css'

interface LocalDXFUploaderProps {
  onFileSelect: (file: File) => void
  onCancel: () => void
}

export function LocalDXFUploader({ onFileSelect, onCancel }: LocalDXFUploaderProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isDragActive, setIsDragActive] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleBrowseClick = () => {
    fileInputRef.current?.click()
  }

  const isValidDXFFile = (file: File): boolean => {
    const fileName = file.name.toLowerCase()
    return fileName.endsWith('.dxf') || fileName.endsWith('.dwg')
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file && isValidDXFFile(file)) {
      setSelectedFile(file)
    }
  }

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragActive(true)
  }

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragActive(false)
  }

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragActive(false)

    const file = e.dataTransfer.files?.[0]
    if (file && isValidDXFFile(file)) {
      setSelectedFile(file)
    }
  }

  const handleUploadClick = () => {
    if (selectedFile) {
      onFileSelect(selectedFile)
    }
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div className={styles.overlay} onClick={onCancel}>
      <div className={styles.container} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>DXF/DWG 파일 업로드</h2>
          <p className={styles.description}>
            CAD 파일을 드래그하거나 클릭해서 찾아보세요. DWG는 맵에 필요 없는 레이어를 지운 DXF로 바꿔서 가져옵니다.
          </p>
        </div>

        {!selectedFile ? (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".dxf,.dwg"
              onChange={handleFileChange}
              className={styles.hiddenInput}
            />
            <div
              className={`${styles.dropZone} ${isDragActive ? styles.dragActive : ''}`}
              onClick={handleBrowseClick}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <div className={styles.uploadIcon}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              </div>
              <p className={styles.uploadText}>
                {isDragActive ? '여기에 파일을 놓으세요' : 'DXF/DWG 파일 선택'}
              </p>
              <p className={styles.uploadHint}>
                {isDragActive ? '놓으면 업로드됩니다' : '또는 이곳에 드래그 앤 드롭'}
              </p>
            </div>
          </>
        ) : (
          <>
            <div className={styles.selectedFileCard}>
              <div className={styles.fileIconCircle}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <path d="M9 15h6" />
                  <path d="M12 18v-6" />
                </svg>
              </div>
              <div className={styles.fileInfo}>
                <p className={styles.fileName}>{selectedFile.name}</p>
                <div className={styles.fileMeta}>
                  <div className={styles.fileMetaItem}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
                      <polyline points="13 2 13 9 20 9" />
                    </svg>
                    <span>{formatFileSize(selectedFile.size)}</span>
                  </div>
                  <div className={styles.fileMetaItem}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    <span>{formatDate(new Date(selectedFile.lastModified))}</span>
                  </div>
                </div>
              </div>
              <div className={styles.successCheckmark}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
            </div>
            <button
              className={styles.changeFileButton}
              onClick={handleBrowseClick}
            >
              다른 파일 선택
            </button>
          </>
        )}

        <div className={styles.actions}>
          <button
            className={styles.cancelButton}
            onClick={onCancel}
          >
            취소
          </button>
          <button
            className={styles.uploadButton}
            onClick={handleUploadClick}
            disabled={!selectedFile}
          >
            <span>파일 업로드</span>
          </button>
        </div>
      </div>
    </div>
  )
}
